import {UtilityService} from '../service/utility.service';

/**
 * @class
 * @description This model contains the properties of a PrismHR PEO Client.
 * @export
 * @name PeoClientApiModel
 */
export class PeoClientApiModel {

  /**
   * @description This method determines the print setup type for the client.
   * @memberof PeoClientApiModel
   * @method
   * @name PeoClientApiModel.DetermineSetup()
   * @param {PeoClientApiModel} client The instance of the PeoClientApiModel to determine the print setup type for.
   * @public
   * @returns {PeoClientApiModel} Returns the instance of the PeoClientApiModel with the print setup type set.
   * @static
   */
  public static DetermineSetup(client: PeoClientApiModel): PeoClientApiModel {

    // Reset the setup name into the client model based on client flags.
    client.printSetup = client.printSettingsSetUpForCustom
      ? 'Custom'
      : client.printSettingsSetUpForPrismHr
        ? 'PrismHR'
        : client.printSettingsSetUpForStandard
          ? 'Standard'
          : 'Not Set Up';

    // We're done, return the client model
    return client;
  }

  /**
   * @description This method creates a new instance of the PeoClientApiPrintSettingsModel class with no setup.
   * @memberof PeoClientApiModel
   * @method
   * @name PeoClientApiModel.None()
   * @param {string} [peoId] The unique PrismHR identifier for the PEO the client-customer is associated with.
   * @param {string} [clientId] The unique PrismHR identifier for the client-customer.
   * @param {number} [customerId] The unique SyncStream identifier for the client-customer.
   * @public
   * @returns {PeoClientApiModel} Returns a new instance of the PeoClientApiPrintSettingsModel class with no setup.
   * @static
   */
  public static None(peoId?: string, clientId?: string, customerId?: number): PeoClientApiModel {

    // Instantiate our response model.
    const model: PeoClientApiModel = new PeoClientApiModel();

    // Check for a provided customer id and set it into our response model if provided.
    if (!!customerId) model.customerId = customerId;

    // Check for a provided client id and set it into our response model if provided.
    if (!!clientId) model.clientId = clientId;

    // Check for a provided peo id and set it into our response model if provided.
    if (!!peoId) model.peoId = peoId;

    // Reset the no-setup flag on the instance.
    model.printSettingsNotSetUp = true;

    // We're done, return the instance.
    return model;
  }

  /**
   * @default false
   * @description This property denotes whether the client has paid for and enabled the California E-File service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#californiaEFilePaid
   * @property
   * @public
   * @type {boolean}
   */
  public californiaEFilePaid: boolean = false;

  /**
   * @description This property contains the unique identifier of the client from the PrismHR system.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#clientId
   * @property
   * @public
   * @type {string}
   */
  public clientId!: string;

  /**
   * @default 0
   * @description This property contains the number of year-end documents the client has corrected.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#correctedDocuments
   * @property
   * @public
   * @type {number}
   */
  public correctedDocuments: number = 0;

  /**
   * @default UtilityService.DateTimeUtc()
   * @description This property contains timestamp at which the PrismHR PEO Client was created in the SyncStream system.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#created
   * @property
   * @public
   * @type {Date}
   */
  public created: Date = UtilityService.DateTimeUtc();

  /**
   * @description This property contains the brand name of the customer in the SyncStream system in which the PrismHR PEO Client is associated.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#customerBrandName
   * @property
   * @public
   * @type {string}
   */
  public customerBrandName: string = 'Inactive';

  /**
   * @description This property contains the unique identifier of the customer in the SyncStream system in which the PrismHR PEO Client is associated.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#customerId
   * @property
   * @public
   * @type {number}
   */
  public customerId!: number;

  /**
   * @description This property contains the name of the customer in the SyncStream system in which the PrismHR PEO Client is associated.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#customerName
   * @property
   * @public
   * @type {string}
   */
  public customerName: string = 'Inactive';

  /**
   * @default false
   * @description This property denotes whether the client has paid for and enabled the Washington DC E-File service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#districtOfColumbiaEFilePaid
   * @property
   * @public
   * @type {boolean}
   */
  public districtOfColumbiaEFilePaid: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client's documents are appropriately split for the viewer or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#documentsSplitForViewer
   * @property
   * @public
   * @type {boolean}
   */
  public documentsSplitForViewer: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client has paid for and enabled the E-File service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#efilePaid
   * @property
   * @public
   * @type {boolean}
   */
  public efilePaid: boolean = false;

  /**
   * @default null
   * @description This property contains the timestamp at which the client last previewed their year-end documents.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#lastPreviewed
   * @property
   * @public
   * @type {Date | null}
   */
  public lastPreviewed: Date | null = null;

  /**
   * @default null
   * @description This property contains the timestamp at which the client last printed their year-end documents.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#lastPrinted
   * @property
   * @public
   * @type {Date | null}
   */
  public lastPrinted: Date | null = null;

  /**
   * @default false
   * @description This property denotes whether the client has paid for and enabled the New Jersey E-File service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#newJerseyEFilePaid
   * @property
   * @public
   * @type {boolean}
   */
  public newJerseyEFilePaid: boolean = false;

  /**
   * @description This property contains the unique identifier of the PEO in the SyncStream system.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#peoId
   * @property
   * @public
   * @type {string}
   */
  public peoId!: string;

  /**
   * @default 0
   * @description This property contains the number of year-end documents the client has previewed.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#previewDocuments
   * @property
   * @public
   */
  public previewDocuments: number = 0;

  /**
   * @default 0
   * @description This property contains the number of year-end documents the client has printed.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printDocuments
   * @property
   * @public
   * @type {number}
   */
  public printDocuments: number = 0;

  /**
   * @default false
   * @description This property denotes whether the print settings are not set up or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printSettingsNotSetUp
   * @property
   * @public
   * @type {boolean}
   */
  public printSettingsNotSetUp: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client print settings are custom or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printSettingsSetUpForCustom
   * @property
   * @public
   * @type {boolean}
   */
  public printSettingsSetUpForCustom: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the print settings are set up for PrismHR or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printSettingsSetUpForPrismHr
   * @property
   * @public
   * @type {boolean}
   */
  public printSettingsSetUpForPrismHr: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the print settings are set up for standard/default or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printSettingsSetUpForStandard
   * @property
   * @public
   * @type {boolean}
   */
  public printSettingsSetUpForStandard: boolean = false;

  /**
   * @default 'Not Set Up'
   * @description This property contains the print setup type for the client.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printSetup
   * @property
   * @public
   * @type {('Custom' | 'Not Set Up' | 'PrismHR' | 'Standard')}
   */
  public printSetup: ('Custom' | 'Not Set Up' | 'PrismHR' | 'Standard') = 'Not Set Up';

  /**
   * @default false
   * @description This property denotes whether the client print to mail laser cut enabled or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printToMailLaserCut
   * @property
   * @public
   * @type {boolean}
   */
  public printToMailLaserCut: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client has paid for print to mail service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printToMailPaid
   * @property
   * @public
   * @type {boolean}
   */
  public printToMailPaid: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client print to mail pressure sealed enabled or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#printToMailPressureSealed
   * @property
   * @public
   * @type {boolean}
   */
  public printToMailPressureSealed: boolean = false;

  /**
   * @default 0
   * @description This property contains the number of year-end documents the client has reprinted.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#reprintDocuments
   * @property
   * @public
   * @type {number}
   */
  public reprintDocuments: number = 0;

  /**
   * @default false
   * @description This property denotes whether the client has paid for and enabled the Rhode Island E-File service or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#rhodeIslandEFilePaid
   * @property
   * @public
   * @type {boolean}
   */
  public rhodeIslandEFilePaid: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client self print laser cut enabled or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#selfPrintLaserCut
   * @property
   * @public
   * @type {boolean}
   */
  public selfPrintLaserCut: boolean = false;

  /**
   * @default false
   * @description This property denotes whether the client self print pressure sealed enabled or not.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#selfPrintPressureSealed
   * @property
   * @public
   * @type {boolean}
   */
  public selfPrintPressureSealed: boolean = false;

  /**
   * @default UtilityService.DateTimeUtc()
   * @description This property contains the timestamp at which the PrismHR PEO Client was last updated in the SyncStream system.
   * @memberof PeoClientApiModel
   * @name PeoClientApiModel#updated
   * @property
   * @public
   * @type {string}
   */
  public updated: Date = UtilityService.DateTimeUtc();
}
